import React, { useMemo, useState } from 'react';
import { Modal, Input, Button, Empty, Popconfirm, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { List, BarChart2, PieChart as PieChartIcon, Plus, Play, Star } from 'lucide-react';
import {
    BarChart, Bar, Line, ComposedChart, XAxis, YAxis, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import {
    APPOINTMENT_STATUS, CHANNEL_LABEL, OUTCOME_LABEL, PAYMENT_METHOD, SYSTEM_AGENT, appointmentsOf, callCenterApi, formatMoney, invoiceNet, invoiceOfAppointment, invoiceSubtotal, invoicesOf,
    servicePrice, callStatsOf, callsOf, careOf, fmtDuration, hasRecording, useCallCenter,
    type AppointmentStatus, type CallLog, type CareChannel, type Customer, type Outcome, type PaymentMethod,
} from '../../CustomerDemo/callCenterStore';
import { meName } from '../../CustomerDemo/softphone';
import { RecordingModal } from '../../CustomerDemo/components/CallWidgets';
import AppointmentModal, { AppointmentStatusBadge } from '../../CustomerDemo/components/AppointmentModal';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#0ea5e9', '#ec4899', '#8b5cf6', '#14b8a6'];
const darkTip = { contentStyle: { backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }, itemStyle: { color: '#fff' } };
const million = (v: number) => (v >= 1e6 ? `${(v / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}tr` : v ? `${Math.round(v / 1000)}k` : '0');

/** Số liệu chi tiêu tính từ hoá đơn — dùng chung nguồn với tab "Lịch sử hóa đơn" */
function useSpending(customer: Customer) {
    const s = useCallCenter();
    return useMemo(() => {
        const invoices = invoicesOf(s, customer.id).filter(x => !x.cancelled);
        const payments = invoices.flatMap(inv => inv.payments.map(p => ({ ...p, inv })));
        // 12 tháng gần nhất theo ngày thu/hoàn tiền
        const months = Array.from({ length: 12 }, (_, i) => dayjs().subtract(11 - i, 'month').startOf('month'));
        const monthly = months.map(m => ({
            name: m.format('[T]M/YY'),
            spend: payments.filter(p => dayjs(p.at).isSame(m, 'month')).reduce((t, p) => t + p.amount, 0),
        }));
        const avg = monthly.reduce((t, m) => t + m.spend, 0) / 12;
        const max = Math.max(0, ...monthly.map(m => m.spend));
        // Phân bổ tiền thực thu của hoá đơn cho từng dòng theo tỉ trọng giá trị
        const byItem = new Map<string, number>();
        invoices.forEach(inv => {
            const sub = invoiceSubtotal(inv);
            const net = invoiceNet(inv);
            if (!sub || !net) return;
            inv.items.forEach(x => byItem.set(x.name, (byItem.get(x.name) || 0) + (x.qty * x.price / sub) * net));
        });
        const services = Array.from(byItem, ([name, value], i) => ({ name, value: Math.round(value), color: PALETTE[i % PALETTE.length] })).sort((a, b) => b.value - a.value);
        const byMethod = (Object.keys(PAYMENT_METHOD) as PaymentMethod[]).map(m => ({
            key: m, name: PAYMENT_METHOD[m].label, color: PAYMENT_METHOD[m].color,
            value: payments.filter(p => p.method === m).reduce((t, p) => t + p.amount, 0),
        })).filter(x => x.value > 0);
        const turns = invoices.filter(x => invoiceNet(x) > 0).sort((a, b) => a.createdAt - b.createdAt)
            .map(x => ({ name: dayjs(x.createdAt).format('DD/MM'), value: invoiceNet(x), code: x.code }));
        return { invoices, monthly: monthly.map(m => ({ ...m, avg: Math.round(avg) })), max, services, byMethod, turns, total: invoices.reduce((t, x) => t + invoiceNet(x), 0) };
    }, [s, customer.id]);
}

/** Tần suất chăm sóc theo nhân viên — từ cuộc gọi + chăm sóc qua kênh */
function useCareFrequency(customer: Customer) {
    const s = useCallCenter();
    return useMemo(() => {
        const rows = new Map<string, { name: string; calls: number; care: number; last: number; note?: string }>();
        const touch = (who: string, at: number, kind: 'calls' | 'care', note?: string) => {
            if (!who || who === SYSTEM_AGENT) return;
            const r = rows.get(who) || { name: who, calls: 0, care: 0, last: 0 };
            r[kind]++;
            if (at > r.last) { r.last = at; if (note) r.note = note; }
            rows.set(who, r);
        };
        callsOf(s, customer.phones).forEach(c => touch(c.agent, c.startedAt, 'calls', c.note));
        careOf(s, customer.phones).filter(e => e.channel !== 'note').forEach(e => touch(e.author, e.at, 'care', e.content));
        return Array.from(rows.values()).sort((a, b) => b.calls + b.care - (a.calls + a.care)).map((r, i) => ({ ...r, value: r.calls + r.care, color: PALETTE[i % PALETTE.length] }));
    }, [s, customer.phones]);
}

export default function CRMDashboard({ customer }: { customer: Customer; onCall?: () => void }) {
    const [serviceView, setServiceView] = useState('chart');
    const [notesView, setNotesView] = useState('chart');
    const sp = useSpending(customer);
    const care = useCareFrequency(customer);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const data = payload[0].payload;
        return (
            <div className="bg-gray-900/90 rounded-lg p-3 text-white shadow-lg text-sm border-none min-w-[180px]">
                <p className="font-bold mb-2">{label}</p>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-indigo-500 rounded-sm" /><span>Chi tiêu: {formatMoney(data.spend)}</span></div>
                <div className="flex items-center gap-2 mt-1 text-gray-300"><span className="w-2.5 h-0.5 bg-rose-500" /><span>Trung bình: {formatMoney(data.avg)}</span></div>
                {data.spend > 0 && data.spend === sp.max && (
                    <div className="flex items-center gap-1.5 mt-3 text-yellow-400 font-medium"><Star className="w-4 h-4 text-yellow-400" fill="currentColor" /><span>Tháng chi tiêu cao nhất</span></div>
                )}
            </div>
        );
    };
    const noData = (text: string) => <div className="h-full min-h-[180px] flex items-center justify-center"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text} /></div>;

    return (
        <div className="pb-10">

            {/* Row 1: Chi tiêu tháng & Chi tiêu dịch vụ — tính từ hoá đơn */}
            <div className="flex flex-col lg:flex-row gap-4 mb-8 mt-2">
                <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm flex flex-col p-5">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 text-base">
                            <BarChart2 className="text-indigo-500 w-5 h-5" /> Chi tiêu theo tháng
                        </h3>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md">12 tháng gần nhất</span>
                    </div>
                    <div className="h-[220px] w-full">
                        {sp.max > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={sp.monthly} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                    <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                                    <YAxis tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={million} />
                                    <Tooltip cursor={{ fill: 'rgba(30, 58, 138, 0.1)' }} content={<CustomTooltip />} />
                                    <Bar dataKey="spend" radius={[6, 6, 6, 6]} barSize={16}>
                                        {sp.monthly.map((m, i) => <Cell key={i} fill={m.spend === sp.max ? '#4f46e5' : m.spend <= 0 ? '#e5e7eb' : '#a5b4fc'} />)}
                                    </Bar>
                                    <Line type="monotone" dataKey="avg" stroke="#f43f5e" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        ) : noData('Chưa có khoản thu nào trong 12 tháng')}
                    </div>
                </div>

                <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm flex flex-col p-5">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 text-base">
                            <PieChartIcon className="text-indigo-500 w-5 h-5" /> Chi tiêu theo dịch vụ / sản phẩm
                        </h3>
                        <span className="text-xs font-medium text-gray-500">Tổng {formatMoney(sp.total)}</span>
                    </div>
                    {sp.services.length ? (
                        <div className="flex flex-col sm:flex-row items-center justify-between h-auto sm:h-[220px] gap-4">
                            <div className="w-full sm:w-1/2 h-[200px] sm:h-full relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={sp.services} cx="50%" cy="50%" innerRadius="65%" outerRadius="90%" paddingAngle={2} dataKey="value" stroke="none">
                                            {sp.services.map(e => <Cell key={e.name} fill={e.color} />)}
                                        </Pie>
                                        <Tooltip {...darkTip} formatter={(v: number) => formatMoney(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="w-full sm:w-1/2 pl-0 sm:pl-4 flex flex-col justify-center gap-3">
                                {sp.services.slice(0, 6).map(item => (
                                    <div key={item.name} className="flex justify-between items-center text-sm gap-2">
                                        <div className="flex items-center gap-2 min-w-0"><span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} /><span className="text-gray-600 dark:text-gray-300 truncate">{item.name}</span></div>
                                        <span className="font-semibold text-gray-800 dark:text-gray-100 tabular-nums shrink-0">{formatMoney(item.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : noData('Khách chưa phát sinh chi tiêu')}
                </div>
            </div>

            <CallInfoSection customer={customer} />

            <AppointmentSection customer={customer} />

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                    <h3 className="text-xl text-000000e0 serif-title">Chi tiêu theo từng lượt</h3>
                    <div className="bg-gray-200/60 dark:bg-gray-800 p-1 rounded-full flex gap-1">
                        <button type="button" onClick={() => setServiceView('chart')} className={`px-5 py-1.5 rounded-full text-sm font-medium cursor-pointer ${serviceView === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button type="button" onClick={() => setServiceView('list')} className={`px-5 py-1.5 rounded-full text-sm font-medium cursor-pointer ${serviceView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                    {!sp.turns.length ? noData('Chưa có hoá đơn đã thu tiền') : serviceView === 'chart' ? (
                        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            <div className="flex-1 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800 pb-6 lg:pb-0 pr-0 lg:pr-8">
                                <h4 className="text-sm font-medium text-gray-500 mb-6">Số tiền thực thu mỗi hoá đơn</h4>
                                <div className="h-[220px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={sp.turns} margin={{ top: 15, right: 0, left: -20, bottom: 0 }}>
                                            <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={million} />
                                            <Tooltip cursor={{ fill: 'transparent' }} {...darkTip} formatter={(v: number, _n: string, p: any) => [formatMoney(v), p.payload.code]} />
                                            <Bar dataKey="value" fill="#4f46e5" radius={[4, 4, 4, 4]} barSize={28} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="w-full lg:w-[300px] shrink-0 flex flex-col justify-center">
                                <h4 className="text-sm font-medium text-gray-500 mb-6 text-center">Tỷ trọng theo hình thức thanh toán</h4>
                                <div className="flex items-center gap-6">
                                    <div className="h-[140px] w-[140px] relative shrink-0">
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <span className="font-bold text-xl text-gray-800 dark:text-gray-100">{million(sp.total)}</span>
                                            <span className="text-[9px] text-gray-500 font-medium uppercase mt-0.5">Tổng</span>
                                        </div>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={sp.byMethod} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                                    {sp.byMethod.map(e => <Cell key={e.key} fill={e.color} />)}
                                                </Pie>
                                                <Tooltip {...darkTip} formatter={(v: number) => formatMoney(v)} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-4 text-sm flex-1">
                                        {sp.byMethod.map(item => (
                                            <div key={item.key} className="flex justify-between items-center gap-2">
                                                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} /><span className="text-gray-600 dark:text-gray-300 text-xs">{item.name}</span></div>
                                                <span className="font-medium text-gray-800 dark:text-gray-100 text-xs">{million(item.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto table-scroll">
                            <table className="w-full text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                                        <th className="pb-3 font-medium">Ngày</th>
                                        <th className="pb-3 font-medium">Mã HĐ</th>
                                        <th className="pb-3 font-medium w-1/3">Nội dung</th>
                                        <th className="pb-3 font-medium">Hình thức</th>
                                        <th className="pb-3 font-medium text-right">Thực thu</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
                                    {sp.invoices.filter(x => invoiceNet(x) > 0).map(x => (
                                        <tr key={x.id}>
                                            <td className="py-4 font-medium text-gray-600 dark:text-gray-300">{dayjs(x.createdAt).format('DD/MM/YYYY')}</td>
                                            <td className="py-4 text-blue-600">{x.code}</td>
                                            <td className="py-4 text-gray-800 dark:text-gray-100">{x.items.map(i => i.name).join(', ')}</td>
                                            <td className="py-4 text-gray-600 dark:text-gray-300">{Array.from(new Set(x.payments.map(p => PAYMENT_METHOD[p.method].label))).join(', ')}</td>
                                            <td className="py-4 text-right font-medium text-gray-800 dark:text-gray-100 tabular-nums">{formatMoney(invoiceNet(x))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={4} className="pt-4 font-bold text-gray-800 dark:text-gray-100 text-sm border-t border-gray-100 dark:border-gray-800">Tổng chi tiêu</td>
                                        <td className="pt-4 font-bold text-gray-800 dark:text-gray-100 text-right border-t border-gray-100 dark:border-gray-800 tabular-nums">{formatMoney(sp.total)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                    <h3 className="text-xl text-000000e0 serif-title">Tần suất chăm sóc theo nhân viên</h3>
                    <div className="bg-gray-200/60 dark:bg-gray-800 p-1 rounded-full flex gap-1">
                        <button type="button" onClick={() => setNotesView('chart')} className={`px-5 py-1.5 rounded-full text-sm font-medium cursor-pointer ${notesView === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button type="button" onClick={() => setNotesView('list')} className={`px-5 py-1.5 rounded-full text-sm font-medium cursor-pointer ${notesView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                    {!care.length ? noData('Chưa có nhân viên nào liên hệ khách') : notesView === 'chart' ? (
                        <div>
                            <h4 className="text-sm font-medium text-gray-500 mb-6">Số lần liên hệ (cuộc gọi + chăm sóc qua kênh)</h4>
                            <div className="h-[220px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={care} margin={{ top: 15, right: 0, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                        <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} {...darkTip} formatter={(v: number, _n: string, p: any) => [`${v} lần (${p.payload.calls} gọi · ${p.payload.care} chăm sóc)`, 'Liên hệ']} />
                                        <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={40}>
                                            {care.map(e => <Cell key={e.name} fill={e.color} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto table-scroll">
                            <table className="w-full text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                                        <th className="pb-3 font-medium">Nhân viên</th>
                                        <th className="pb-3 font-medium text-center">Cuộc gọi</th>
                                        <th className="pb-3 font-medium text-center">Chăm sóc</th>
                                        <th className="pb-3 font-medium">Lần gần nhất</th>
                                        <th className="pb-3 font-medium w-1/3">Nội dung gần nhất</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
                                    {care.map(r => (
                                        <tr key={r.name}>
                                            <td className="py-4 text-gray-800 dark:text-gray-100 font-medium">{r.name}</td>
                                            <td className="py-4 text-indigo-600 font-bold text-center">{r.calls}</td>
                                            <td className="py-4 text-teal-600 font-bold text-center">{r.care}</td>
                                            <td className="py-4 text-gray-600 dark:text-gray-300">{dayjs(r.last).format('HH:mm DD/MM/YYYY')}</td>
                                            <td className="py-4 text-gray-500 text-xs">{r.note || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}
const OUTCOME_COLOR: Record<Outcome, string> = { answered: '#10b981', no_answer: '#f43f5e', busy: '#f59e0b' };
const OUTCOME_BADGE: Record<Outcome, string> = {
    answered: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    no_answer: 'bg-rose-50 text-rose-600 border-rose-200',
    busy: 'bg-amber-50 text-amber-600 border-amber-200',
};

type CareItem = { id: string; at: number; who: string; text?: string; call: CallLog | null; channel?: CareChannel };

/** Thống kê trạng thái gọi + nội dung chăm sóc — tính từ nhật ký tổng đài của khách */
function CallInfoSection({ customer }: { customer: Customer }) {
    const s = useCallCenter();
    const [adding, setAdding] = useState(false);
    const [text, setText] = useState('');
    const [recording, setRecording] = useState<CallLog | null>(null);
    const stats = callStatsOf(s, customer.phones);
    const pie = (Object.keys(OUTCOME_LABEL) as Outcome[]).map(k => ({ key: k, name: OUTCOME_LABEL[k], value: stats[k], color: OUTCOME_COLOR[k] }));
    const empty = [{ key: 'none', name: 'Chưa có', value: 1, color: '#e5e7eb' }];
    const items: CareItem[] = [
        ...callsOf(s, customer.phones).map(c => ({ id: c.id, at: c.startedAt, who: c.agent, text: c.note, call: c })),
        ...careOf(s, customer.phones).filter(e => e.channel !== 'note').map(e => ({ id: e.id, at: e.at, who: e.author, text: e.content, call: null, channel: e.channel })),
    ].sort((a, b) => b.at - a.at).slice(0, 12);

    const save = () => {
        if (!text.trim()) return;
        callCenterApi.addCare(customer.phones[0], 'care', text.trim(), meName());
        message.success('Đã thêm ghi chú chăm sóc');
        setText('');
        setAdding(false);
    };

    return (
        <div className="mb-8">
            <div className="flex items-baseline gap-2 mb-4 px-1">
                <h3 className="text-xl text-000000e0 serif-title">Thông tin & Lịch sử cuộc gọi</h3>
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="w-full lg:w-[300px] shrink-0 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 flex flex-col">
                    <h4 className="font-bold text-gray-800 dark:text-gray-100 text-sm mb-5 flex items-center gap-2">
                        <PieChartIcon className="text-indigo-500 w-4 h-4" /> Thống kê trạng thái gọi
                    </h4>
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="h-[150px] w-[150px] relative mb-5">
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="font-bold text-2xl text-gray-800 dark:text-gray-100">{stats.total}</span>
                                <span className="text-[10px] text-gray-500 font-medium">CUỘC GỌI</span>
                            </div>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={stats.total ? pie : empty} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                        {(stats.total ? pie : empty).map(e => <Cell key={e.key} fill={e.color} />)}
                                    </Pie>
                                    {stats.total > 0 && <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }} itemStyle={{ color: '#fff' }} />}
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="w-full space-y-2.5 text-sm px-2">
                            {pie.map(p => (
                                <div key={p.key} className="flex justify-between items-center">
                                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} /><span className="text-gray-600 dark:text-gray-300">{p.name}</span></div>
                                    <span className="font-semibold text-gray-800 dark:text-gray-100">{p.value}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
                                <span>Tổng thời gian đàm thoại</span><span className="font-semibold">{fmtDuration(stats.talk)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-w-0 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center shrink-0">
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-2">
                            <List className="text-indigo-500 w-4 h-4" /> Nội dung chăm sóc khách hàng
                        </h4>
                        <button type="button" onClick={() => setAdding(true)} className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-xs font-medium px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 border border-indigo-100 cursor-pointer">
                            <Plus className="w-3.5 h-3.5" /> Thêm ghi chú
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 max-h-[340px] space-y-3">
                        {items.length ? items.map(it => (
                            <div key={it.id} className="flex gap-4">
                                <div className="mt-1.5 shrink-0">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: it.call ? OUTCOME_COLOR[it.call.outcome] : '#6366f1' }} />
                                </div>
                                <div className="flex-1 min-w-0 border border-gray-100 dark:border-gray-800 shadow-sm rounded-lg p-3.5 hover:border-indigo-200 transition-colors">
                                    <div className="flex justify-between items-start mb-1.5 gap-2">
                                        <div className="min-w-0">
                                            <span className="font-bold text-gray-800 dark:text-gray-100 text-sm">{it.who}</span>
                                            <span className="text-xs text-gray-400 font-medium ml-2">{dayjs(it.at).format('HH:mm DD/MM/YYYY')}</span>
                                        </div>
                                        {it.call
                                            ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide shrink-0 ${OUTCOME_BADGE[it.call.outcome]}`}>{OUTCOME_LABEL[it.call.outcome]}</span>
                                            : <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide shrink-0 bg-indigo-50 text-indigo-600 border-indigo-200">{CHANNEL_LABEL[it.channel || 'care']}</span>}
                                    </div>
                                    <p className={`text-sm leading-relaxed whitespace-pre-line ${it.text ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 italic'}`}>{it.text || 'Không có ghi chú'}</p>
                                    {it.call && hasRecording(it.call) && (
                                        <button type="button" onClick={() => setRecording(it.call)} className="mt-2 flex items-center gap-1.5 px-3 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600 hover:border-indigo-300 cursor-pointer">
                                            <Play className="text-indigo-500 w-3 h-3" /> {fmtDuration(it.call.duration)}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có nội dung chăm sóc" />}
                    </div>
                </div>
            </div>
            <Modal open={adding} title="Thêm ghi chú chăm sóc" onCancel={() => setAdding(false)} footer={[
                <Button key="c" onClick={() => setAdding(false)}>Huỷ</Button>,
                <Button key="s" type="primary" disabled={!text.trim()} onClick={save}>Lưu</Button>,
            ]}>
                <Input.TextArea autoFocus value={text} onChange={e => setText(e.target.value)} autoSize={{ minRows: 4 }} placeholder="Nội dung trao đổi với khách..." />
            </Modal>
            <RecordingModal call={recording} onClose={() => setRecording(null)} />
        </div>
    );
}

/** Đổi lịch hẹn: chọn giờ mới → lịch về trạng thái "Lịch mới" chờ xác nhận lại */
function RescheduleButton({ id, at }: { id: string; at: number }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button size="small" type="link" onClick={() => setOpen(true)}>Đổi lịch</Button>
            <DatePicker open={open} onOpenChange={setOpen} showTime={{ format: 'HH:mm', minuteStep: 15 }} format="HH:mm DD/MM/YYYY"
                defaultPickerValue={dayjs(at)} disabledDate={d => d.isBefore(dayjs(), 'day')}
                style={{ position: 'absolute', visibility: 'hidden', width: 0 }}
                onChange={v => {
                    if (!v) return;
                    if (v.isBefore(dayjs())) { message.error('Thời gian hẹn phải ở tương lai'); return; }
                    callCenterApi.rescheduleAppointment(id, v.valueOf(), meName());
                    message.success(`Đã đổi lịch sang ${v.format('HH:mm DD/MM/YYYY')} — cần xác nhận lại với khách`);
                }} />
        </>
    );
}

/** Lịch hẹn của khách: biểu đồ theo tháng + tỉ lệ trạng thái, danh sách có thao tác xác nhận / hoàn thành / huỷ */
function AppointmentSection({ customer }: { customer: Customer }) {
    const s = useCallCenter();
    const [view, setView] = useState<'chart' | 'list'>('list');
    const [booking, setBooking] = useState(false);
    const list = appointmentsOf(s, customer.id);
    const year = dayjs().year();
    const months = Array.from({ length: 12 }, (_, i) => ({
        name: `T${i + 1}`,
        count: list.filter(a => dayjs(a.at).year() === year && dayjs(a.at).month() === i).length,
    }));
    const byStatus = (Object.keys(APPOINTMENT_STATUS) as AppointmentStatus[])
        .map(k => ({ key: k, name: APPOINTMENT_STATUS[k].label, value: list.filter(a => a.status === k).length, color: APPOINTMENT_STATUS[k].color }));
    const now = Date.now();

    const act = (id: string, status: AppointmentStatus, msg: string) => {
        const autos = callCenterApi.setAppointmentStatus(id, status, meName());
        message.success(autos ? `${msg} · tự tạo ${autos} lịch gọi${status === 'done' ? ' chăm sóc (hỏi thăm + khảo sát) và hoá đơn chờ thu tiền' : ' lại'}` : msg);
    };

    return (
        <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                <h3 className="text-xl text-000000e0 serif-title">Lịch hẹn ({list.length})</h3>
                <div className="flex items-center gap-2">
                    <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => setBooking(true)}>Tạo lịch hẹn</Button>
                    <div className="bg-gray-200/60 dark:bg-gray-800 p-1 rounded-full flex gap-1">
                        <button type="button" onClick={() => setView('chart')} className={`px-4 py-1 rounded-full text-sm font-medium cursor-pointer ${view === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button type="button" onClick={() => setView('list')} className={`px-4 py-1 rounded-full text-sm font-medium cursor-pointer ${view === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                {!list.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Khách chưa có lịch hẹn nào">
                        <Button type="primary" onClick={() => setBooking(true)}>Tạo lịch hẹn</Button>
                    </Empty>
                ) : view === 'chart' ? (
                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                        <div className="flex-1 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800 pb-6 lg:pb-0 lg:pr-8">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">Số lịch hẹn theo tháng ({year})</h4>
                            <div className="h-[200px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={months} margin={{ top: 10, right: 0, left: -30, bottom: 0 }}>
                                        <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                                        <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                                        <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }} itemStyle={{ color: '#fff' }} formatter={(v: number) => [v, 'Lịch hẹn']} />
                                        <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 4, 4]} barSize={14} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="w-full lg:w-[280px] shrink-0 flex flex-col justify-center">
                            <h4 className="text-sm font-medium text-gray-500 mb-4 text-center">Tỷ lệ theo trạng thái</h4>
                            <div className="flex items-center gap-5">
                                <div className="h-[130px] w-[130px] relative shrink-0">
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <span className="font-bold text-xl text-gray-800 dark:text-gray-100">{list.length}</span>
                                        <span className="text-[9px] text-gray-500 font-medium uppercase mt-0.5">Lịch hẹn</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={byStatus.filter(x => x.value)} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                                {byStatus.filter(x => x.value).map(e => <Cell key={e.key} fill={e.color} />)}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="space-y-2.5 text-xs flex-1">
                                    {byStatus.map(it => (
                                        <div key={it.key} className="flex justify-between items-center">
                                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: it.color }} /><span className="text-gray-600 dark:text-gray-300">{it.name}</span></div>
                                            <span className="font-medium text-gray-800 dark:text-gray-100">{it.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[820px]">
                            <thead>
                                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                                    <th className="pb-3 font-medium">Thời gian</th>
                                    <th className="pb-3 font-medium">Dịch vụ</th>
                                    <th className="pb-3 font-medium">Chi nhánh</th>
                                    <th className="pb-3 font-medium">Giá trị</th>
                                    <th className="pb-3 font-medium">Người đặt</th>
                                    <th className="pb-3 font-medium">Trạng thái</th>
                                    <th className="pb-3 font-medium text-right">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
                                {list.map(a => {
                                    const active = a.status === 'new' || a.status === 'confirmed';
                                    const past = a.at < now;
                                    return (
                                        <tr key={a.id}>
                                            <td className="py-3 font-medium text-gray-700 dark:text-gray-200">
                                                {dayjs(a.at).format('HH:mm DD/MM/YYYY')}
                                                {active && past && <div className="text-[11px] text-rose-500 font-normal">Đã qua giờ hẹn — cập nhật kết quả</div>}
                                            </td>
                                            <td className="py-3 text-gray-800 dark:text-gray-100">{a.service}{a.note && <div className="text-xs text-gray-400">{a.note}</div>}</td>
                                            <td className="py-3 text-gray-600 dark:text-gray-300">{a.branch}</td>
                                            <td className="py-3 text-gray-700 dark:text-gray-200 tabular-nums">
                                                {formatMoney(a.price ?? servicePrice(a.service))}
                                                {a.status === 'done' && (() => {
                                                    const inv = invoiceOfAppointment(s, a.id);
                                                    return <div className={`text-[11px] ${inv ? 'text-gray-400' : 'text-amber-600'}`}>{inv ? `HĐ ${inv.code}` : 'Chưa lập hoá đơn'}</div>;
                                                })()}
                                            </td>
                                            <td className="py-3 text-gray-600 dark:text-gray-300">{a.by}</td>
                                            <td className="py-3"><AppointmentStatusBadge status={a.status} /></td>
                                            <td className="py-3 text-right whitespace-nowrap">
                                                {a.status === 'new' && <Button size="small" type="link" onClick={() => act(a.id, 'confirmed', 'Đã xác nhận lịch hẹn')}>Xác nhận</Button>}
                                                {active && past && <Button size="small" type="link" onClick={() => act(a.id, 'done', 'Đã ghi nhận khách đến làm')}>Đã đến làm</Button>}
                                                {active && past && <Button size="small" type="link" className="!text-slate-500" onClick={() => act(a.id, 'no_show', 'Đã ghi nhận khách không đến')}>Không đến</Button>}
                                                {active && <RescheduleButton id={a.id} at={a.at} />}
                                                {active && (
                                                    <Popconfirm title="Huỷ lịch hẹn này?" okText="Huỷ lịch" cancelText="Không" okButtonProps={{ danger: true }} onConfirm={() => act(a.id, 'cancelled', 'Đã huỷ lịch hẹn')}>
                                                        <Button size="small" type="link" danger>Huỷ</Button>
                                                    </Popconfirm>
                                                )}
                                                {!active && <span className="text-xs text-gray-300">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <AppointmentModal customer={customer} open={booking} onClose={() => setBooking(false)} />
        </div>
    );
}
